import { motion } from "framer-motion";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import dayjs from "dayjs";
import { SectionHeader } from "./SectionHeader";
import { inrCompact, inrFull, prettyLoc, LOC_COLORS } from "@/lib/format";

const EASE = [0.16, 1, 0.3, 1];

const ADD_METRICS = [
  { key: "turnover", label: "Total Turnover" },
  { key: "gold", label: "Gold" },
  { key: "diamond", label: "Diamond & Colour Stone" },
  { key: "precious", label: "Precious & Uncut" },
  { key: "silver", label: "Silver" },
  { key: "coins", label: "Coins" },
  { key: "excl_coins", label: "Turnover excl. Coins" },
  { key: "gp", label: "Gross Profit" },
  { key: "royalty", label: "Royalty" },
  { key: "gp_after_royalty", label: "GP after Royalty" },
];

const gapVsBest = (v, best) => {
  if (best == null || !isFinite(best) || best === 0) return null;
  if (v === best) return "best";
  return `▼ ${(Math.abs((best - v) / Math.abs(best)) * 100).toFixed(1)}% vs best`;
};

const buildRows = (stats) => {
  const rows = [];
  for (const m of ADD_METRICS) {
    const vals = stats.map((s) => s[m.key] || 0);
    const total = vals.reduce((a, b) => a + b, 0);
    const best = Math.max(...vals);
    rows.push({
      id: m.key,
      label: m.label,
      cells: vals.map((v) => ({
        text: inrCompact(v),
        sub: [total > 0 ? `${((v / total) * 100).toFixed(1)}% of total` : null, gapVsBest(v, best)].filter(Boolean).join(" · "),
        leader: v === best && total > 0,
      })),
    });
  }

  const ratioRow = (id, label, sub, fn, fmt) => {
    const vals = stats.map(fn);
    const valid = vals.filter((v) => v != null && isFinite(v));
    const best = valid.length ? Math.max(...valid) : null;
    return {
      id,
      label,
      sub,
      cells: vals.map((v) => ({
        text: v != null && isFinite(v) ? fmt(v) : "—",
        sub: v != null && isFinite(v) ? gapVsBest(v, best) : null,
        leader: v != null && v === best,
      })),
    };
  };

  rows.push(ratioRow("avg-daily", "Avg Daily Sale", null, (s) => s.avg_daily || null, inrCompact));
  rows.push(ratioRow("gp-margin", "GP Margin", "GP ÷ Turnover excl. Coins", (s) => (s.excl_coins ? (s.gp / s.excl_coins) * 100 : null), (v) => `${v.toFixed(1)}%`));
  rows.push(
    ratioRow("gp-after-royalty-margin", "GP After Royalty Margin", "GP after Royalty ÷ Turnover excl. Coins", (s) => (s.excl_coins ? (s.gp_after_royalty / s.excl_coins) * 100 : null), (v) => `${v.toFixed(1)}%`)
  );
  rows.push(ratioRow("stock-turn", "Stock Turn (annualised)", "avg daily × 365 ÷ stock", (s) => s.stock_turn, (v) => `${v.toFixed(2)}x`));

  const csVals = stats.map((s) => s.closing_stock?.value ?? null);
  const csTotal = csVals.reduce((a, b) => a + (b || 0), 0);
  const csBest = Math.max(...csVals.filter((v) => v != null), -Infinity);
  rows.push({
    id: "closing-stock",
    label: "Closing Stock",
    cells: stats.map((s, i) => ({
      text: s.closing_stock ? inrCompact(s.closing_stock.value) : "—",
      sub: s.closing_stock
        ? [
            `as of ${dayjs(s.closing_stock.date).format("DD MMM YY")}`,
            csTotal > 0 ? `${(((csVals[i] || 0) / csTotal) * 100).toFixed(1)}% of total` : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : null,
      leader: csVals[i] != null && csVals[i] === csBest,
    })),
  });

  rows.push({
    id: "ledger-balance",
    label: "Ledger Balance",
    cells: stats.map((s) => ({
      text: s.ledger_balance ? inrCompact(s.ledger_balance.value) : "—",
      sub: s.ledger_balance ? `as of ${dayjs(s.ledger_balance.date).format("DD MMM YY")}` : null,
      neg: s.ledger_balance?.value < 0,
    })),
  });

  const bdVals = stats.map((s) => s.best_day?.value ?? null);
  const bdBest = Math.max(...bdVals.filter((v) => v != null), -Infinity);
  rows.push({
    id: "best-day",
    label: "Best Day",
    cells: stats.map((s, i) => ({
      text: s.best_day ? inrCompact(s.best_day.value) : "—",
      sub: s.best_day ? dayjs(s.best_day.date).format("DD MMM YY") : null,
      leader: bdVals[i] != null && bdVals[i] === bdBest,
    })),
  });

  return rows;
};

const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-md px-4 py-3 text-xs">
      <p className="font-semibold text-slate-900 mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-slate-600 tabular-nums">
          <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
          {prettyLoc(p.dataKey)}: <span className="font-semibold text-slate-900">{inrFull(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const cellCls = (c) => {
  if (c.neg) return "text-red-600 font-semibold";
  if (c.leader) return "font-bold text-slate-900 bg-amber-50/50";
  return "text-slate-600";
};

export const CompareSection = ({ data }) => {
  const stats = data?.location_stats || [];
  const monthly = data?.location_monthly || [];
  if (stats.length < 2) return null;
  const locs = stats.map((s) => s.location);
  const rows = buildRows(stats);

  return (
    <section id="compare" data-testid="compare-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14">
      <SectionHeader number="03" title="Compare" subtitle="Location head-to-head." />

      <div className="grid grid-cols-1 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="hidden md:block bg-white border border-slate-200 shadow-sm overflow-x-auto"
          data-testid="comparison-table"
        >
          <table className="w-full text-sm" style={{ minWidth: 260 + locs.length * 190 }}>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/60">
                <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">Metric</th>
                {locs.map((l, i) => (
                  <th key={l} className="px-5 py-4 text-right text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap" style={{ color: LOC_COLORS[i % LOC_COLORS.length] }}>
                    {prettyLoc(l)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`compare-row-${r.id}`}>
                  <td className="px-5 py-3 text-slate-500">
                    {r.label}
                    {r.sub && <span className="block text-[10px] text-slate-400">{r.sub}</span>}
                  </td>
                  {r.cells.map((c, i) => (
                    <td key={locs[i]} className={`px-5 py-3 text-right tabular-nums whitespace-nowrap ${cellCls(c)}`}>
                      {c.text}
                      {c.sub && <span className="block text-[10px] font-normal text-slate-400">{c.sub}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="md:hidden bg-white border border-slate-200 shadow-sm divide-y divide-slate-100"
          data-testid="comparison-cards"
        >
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-3" data-testid={`compare-card-${r.id}`}>
              <p className="text-xs font-semibold text-slate-500">{r.label}</p>
              {r.sub && <p className="text-[10px] text-slate-400">{r.sub}</p>}
              <div className="mt-1.5 space-y-1.5">
                {r.cells.map((c, i) => (
                  <div key={locs[i]} className="flex items-start justify-between gap-2">
                    <span className="text-[11px] font-semibold" style={{ color: LOC_COLORS[i % LOC_COLORS.length] }}>
                      {prettyLoc(locs[i])}
                    </span>
                    <span className="text-right">
                      <span className={`text-sm tabular-nums ${cellCls(c).replace("bg-amber-50/50", "")}`}>{c.text}</span>
                      {c.sub && <span className="block text-[10px] text-slate-400">{c.sub}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="bg-white border border-slate-200 p-6 shadow-sm"
          data-testid="location-comparison-chart"
        >
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-6">
            Monthly Turnover by Location
          </p>
          <div className="h-[320px] sm:h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 5, right: 5, left: 0, bottom: 0 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                <YAxis tickFormatter={inrCompact} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} width={76} />
                <Tooltip content={<BarTooltip />} cursor={{ fill: "#F8FAFC" }} />
                <Legend formatter={(v) => <span className="text-xs text-slate-600">{prettyLoc(v)}</span>} />
                {locs.map((loc, i) => (
                  <Bar key={loc} dataKey={loc} fill={LOC_COLORS[i % LOC_COLORS.length]} radius={[2, 2, 0, 0]} maxBarSize={22} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
